// src/index.ts
import createClient from "openapi-fetch";

// src/errors.ts
var ApiRequestError = class extends Error {
  constructor(status, body) {
    super(body?.error.message ?? `Request failed with status ${status}`);
    this.status = status;
    this.body = body;
    this.name = "ApiRequestError";
  }
  status;
  body;
  /** The API error code; `network` when no response arrived, `internal` for an unexpected body. */
  get code() {
    if (this.body?.error.code) return this.body.error.code;
    return this.status === 0 ? "network" : "internal";
  }
};
function errorMessage(err) {
  if (err instanceof ApiRequestError) {
    if (err.status === 429) return "Too many tries. Wait a minute and try again.";
    if (err.code === "network")
      return "You seem to be offline. Check your connection and try again.";
    if (!err.body) return "Something went wrong. Please try again.";
    return err.message;
  }
  if (err instanceof TypeError)
    return "You seem to be offline. Check your connection and try again.";
  return "Something went wrong. Please try again.";
}
function authErrorMessage(message) {
  if (/invalid login credentials/i.test(message)) return "That email or password is wrong.";
  if (/email not confirmed/i.test(message))
    return "Confirm your email first. Use the link we sent you.";
  if (/already registered|already exists/i.test(message))
    return "An account with this email already exists. Sign in instead.";
  if (/weak|pwned|leaked/i.test(message))
    return "Pick a stronger password. Avoid common or reused passwords.";
  if (/not authorized/i.test(message))
    return "We cannot send email to this address yet. Please try again later.";
  if (/rate|too many|seconds/i.test(message)) return "Too many tries. Wait a minute and try again.";
  if (/same.*password|different from the old/i.test(message))
    return "Choose a password you have not used here before.";
  if (/expired|invalid/i.test(message)) return "This link has expired. Request a new one.";
  return "Something went wrong. Check your connection and try again.";
}

// src/index.ts
function createApiClient(options) {
  const client = createClient({ baseUrl: options.baseUrl, fetch: options.fetch });
  const auth = {
    async onRequest({ request }) {
      const token = await options.getAccessToken();
      if (token) request.headers.set("authorization", `Bearer ${token}`);
      return request;
    }
  };
  client.use(auth);
  return client;
}
async function unwrap(promise) {
  const { data, error, response } = await promise;
  if (!response.ok || error !== void 0) {
    throw new ApiRequestError(response.status, error ?? null);
  }
  return data;
}
export {
  ApiRequestError,
  authErrorMessage,
  createApiClient,
  errorMessage,
  unwrap
};
